import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Stack,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  ArrowBack,
  Route as RouteIcon,
  Terrain,
  Timeline,
  Flag,
  TrendingUp,
  TrendingDown,
  Info,
  Map as MapIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Area,
  AreaChart
} from 'recharts';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

// Set Mapbox access token
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

// Debug logging for Mapbox token
console.log('🗺️ Mapbox Debug - Access token loaded:', mapboxgl.accessToken ? 'YES' : 'NO');
console.log('🗺️ Mapbox Debug - Token length:', mapboxgl.accessToken.length);
console.log('🗺️ Mapbox Debug - Environment variable:', process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ? 'FOUND' : 'MISSING');

// Add CSS animations for markers
const mapStyles = `
  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 0.8;
    }
    50% {
      transform: scale(1.3);
      opacity: 0.4;
    }
    100% {
      transform: scale(1);
      opacity: 0.8;
    }
  }

  .waypoint-marker {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .mapboxgl-popup-content {
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    border: none;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }

  .mapboxgl-popup-anchor-top .mapboxgl-popup-tip,
  .mapboxgl-popup-anchor-top-left .mapboxgl-popup-tip,
  .mapboxgl-popup-anchor-top-right .mapboxgl-popup-tip {
    border-bottom-color: white;
  }

  .mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip,
  .mapboxgl-popup-anchor-bottom-left .mapboxgl-popup-tip,
  .mapboxgl-popup-anchor-bottom-right .mapboxgl-popup-tip {
    border-top-color: white;
  }

  .mapboxgl-popup-anchor-left .mapboxgl-popup-tip {
    border-right-color: white;
  }

  .mapboxgl-popup-anchor-right .mapboxgl-popup-tip {
    border-left-color: white;
  }
`;

// Inject styles into the document head
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = mapStyles;
  document.head.appendChild(styleSheet);
}

// Create waypoint marker element for Mapbox with enhanced styling
const createWaypointElement = (type: string, waypoint: any): HTMLElement => {
  const el = document.createElement('div');
  el.className = 'waypoint-marker';

  // Create marker container
  const markerContainer = document.createElement('div');
  markerContainer.style.position = 'relative';
  markerContainer.style.display = 'flex';
  markerContainer.style.alignItems = 'center';
  markerContainer.style.justifyContent = 'center';
  markerContainer.style.transition = 'all 0.3s ease';

  if (type === 'start' || type === 'finish') {
    // Circle markers for start/finish
    const circleColor = type === 'start' ? '#22C55E' : '#000000'; // Green for start, black for finish
    markerContainer.style.backgroundColor = circleColor;
    markerContainer.style.width = '24px';
    markerContainer.style.height = '24px';
    markerContainer.style.borderRadius = '50%';
    markerContainer.style.border = '3px solid white';
    markerContainer.style.display = 'flex';
    markerContainer.style.alignItems = 'center';
    markerContainer.style.justifyContent = 'center';
    markerContainer.style.color = 'white';
    markerContainer.style.fontSize = '12px';
    markerContainer.style.fontWeight = 'bold';
    markerContainer.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
    markerContainer.textContent = type === 'start' ? 'S' : 'F';
  } else {
    // Enhanced waypoint markers
    const colors = {
      feed_zone: '#FF9800',
      sprint: '#9C27B0',
      kom: '#F44336',
      checkpoint: '#2196F3'
    };

    const iconColor = colors[type as keyof typeof colors] || '#2196F3';

    markerContainer.style.backgroundColor = iconColor;
    markerContainer.style.width = '24px';
    markerContainer.style.height = '24px';
    markerContainer.style.borderRadius = '50%';
    markerContainer.style.border = '3px solid white';
    markerContainer.style.display = 'flex';
    markerContainer.style.alignItems = 'center';
    markerContainer.style.justifyContent = 'center';
    markerContainer.style.color = 'white';
    markerContainer.style.fontSize = '12px';
    markerContainer.style.fontWeight = 'bold';
    markerContainer.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
    markerContainer.textContent = getWaypointSymbol(type);
  }

  // Add hover effects
  el.addEventListener('mouseenter', () => {
    markerContainer.style.transform = 'scale(1.2)';
    markerContainer.style.zIndex = '1000';
  });

  el.addEventListener('mouseleave', () => {
    markerContainer.style.transform = 'scale(1)';
    markerContainer.style.zIndex = 'auto';
  });

  el.style.cursor = 'pointer';
  el.appendChild(markerContainer);

  return el;
};

const getWaypointSymbol = (type: string): string => {
  switch (type.toLowerCase()) {
    case 'start': return 'S';
    case 'finish': return 'F';
    case 'feed_zone': return 'Z';
    case 'sprint': return 'P';
    case 'kom': return 'K';
    default: return 'W';
  }
};

// Direction arrows feature removed

interface RouteData {
  raceId: string;
  name: string;
  totalDistance: number;
  elevationProfile: {
    totalGain: number;
    totalLoss: number;
    maxElevation: number;
    minElevation: number;
  };
  coordinates: Array<{
    lat: number;
    lng: number;
    elevation: number;
    distance?: number;
  }>;
  waypoints: Array<{
    name: string;
    type: string;
    lat: number;
    lng: number;
    distance?: number;
  }>;
  segments: Array<{
    name: string;
    startDistance: number;
    endDistance: number;
    distance: number;
    averageGrade: number;
    maxGrade: number;
    elevationGain: number;
    difficulty: number;
    type: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

const RouteViewer: React.FC = () => {
  const { raceId } = useParams<{ raceId: string }>();
  const navigate = useNavigate();
  const [route, setRoute] = useState<RouteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showWaypoints, setShowWaypoints] = useState(false);
  // Removed direction arrows feature

  // Mapbox refs and state
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const waypointMarkers = useRef<mapboxgl.Marker[]>([]);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${process.env.REACT_APP_API_URL}/routes/${raceId}`);

        if (!response.ok) {
          throw new Error('Failed to fetch route data');
        }

        const result = await response.json();

        if (!result.data || !result.data.route) {
          throw new Error('Invalid route data structure received from API');
        }

        const routeData = result.data.route;
        const transformedRoute = {
          raceId: result.data.raceId,
          name: result.data.raceId,
          totalDistance: routeData.totalDistance,
          coordinates: routeData.elevationProfile || [],
          elevationProfile: {
            totalGain: routeData.elevationStats?.gain || 0,
            totalLoss: routeData.elevationStats?.loss || 0,
            maxElevation: routeData.elevationStats?.maxElevation || 0,
            minElevation: routeData.elevationStats?.minElevation || 0
          },
          waypoints: (routeData.waypoints || []).map((waypoint: any) => ({
            ...waypoint,
            lat: waypoint.position?.lat || 0,
            lng: waypoint.position?.lng || 0,
            distance: waypoint.distanceFromStart || 0
          })),
          segments: (routeData.segments || []).map((segment: any) => ({
            ...segment,
            averageGrade: segment.averageGradient,
            distance: segment.endDistance - segment.startDistance
          })),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        setRoute(transformedRoute);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load route');
      } finally {
        setLoading(false);
      }
    };

      if (raceId) {
        fetchData();
      }
    }
  }, [raceId]);

  // Initialize Mapbox map
  useEffect(() => {
    console.log('🗺️ Mapbox Debug - useEffect triggered');
    console.log('🗺️ Mapbox Debug - mapContainer.current:', mapContainer.current);
    console.log('🗺️ Mapbox Debug - map.current:', map.current);

    // Clean up any existing map instance first
    if (map.current) {
      console.log('🗺️ Mapbox Debug - Cleaning up existing map instance');
      map.current.remove();
      map.current = null;
      setMapLoaded(false);
    }

    const createMap = () => {
      console.log('🗺️ Mapbox Debug - Creating new Mapbox map...');

      try {
        map.current = new mapboxgl.Map({
          container: mapContainer.current!,
          style: 'mapbox://styles/wedobroadcast/cmftsafww00j801sd8ifhcmir',
          center: [101.9758, 4.2105], // Center of Malaysia
          zoom: 6, // Good overview of Malaysia
          maxZoom: 18,
          minZoom: 3,
          antialias: true,
          attributionControl: false,
          logoPosition: 'bottom-right'
        });

        console.log('🗺️ Mapbox Debug - Map object created successfully');
      } catch (error) {
        console.error('🗺️ Mapbox Debug - Error creating map:', error);
        return;
      }

    // Add navigation controls
    const nav = new mapboxgl.NavigationControl({
      showCompass: true,
      showZoom: true,
      visualizePitch: true
    });
    map.current.addControl(nav, 'top-right');

    // Add fullscreen control
    const fullscreen = new mapboxgl.FullscreenControl();
    map.current.addControl(fullscreen, 'top-right');

    // Add scale control
    const scale = new mapboxgl.ScaleControl({
      maxWidth: 100,
      unit: 'metric'
    });
    map.current.addControl(scale, 'bottom-left');


    console.log('🗺️ Mapbox Debug - Map created successfully, adding event listeners...');

    // Add error handler
    map.current.on('error', (e) => {
      console.error('🗺️ Mapbox Debug - Map error:', e);
    });

    map.current.on('load', () => {
      console.log('🗺️ Mapbox Debug - Map loaded successfully!');
      setMapLoaded(true);
    });

      const handleResize = () => {
        if (map.current) {
          map.current.resize();
        }
      };

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        if (map.current) {
          map.current.remove();
          map.current = null;
        }
      };
    };

    const initializeMap = () => {
      console.log('🗺️ Mapbox Debug - Attempting map initialization');

      if (!mapContainer.current) {
        console.log('🗺️ Mapbox Debug - mapContainer.current is null, retrying...');
        // Retry after a short delay if DOM isn't ready
        setTimeout(initializeMap, 100);
        return;
      }

      if (map.current) {
        console.log('🗺️ Mapbox Debug - Map already exists, skipping creation');
        return;
      }

      console.log('🗺️ Mapbox Debug - DOM element found, proceeding with map creation');
      createMap();
    };

    // Use timeout to ensure DOM is ready
    const timeoutId = setTimeout(initializeMap, 200);

    return () => {
      clearTimeout(timeoutId);
      if (map.current) {
        console.log('🗺️ Mapbox Debug - Cleaning up map on unmount');
        map.current.remove();
        map.current = null;
        setMapLoaded(false);
      }
    };
  }, []); // Run once on mount

  // Convert route data to GeoJSON format
  const convertRouteToGeoJSON = (routeData: RouteData) => {
    return {
      type: 'Feature' as const,
      properties: {},
      geometry: {
        type: 'LineString' as const,
        coordinates: routeData.coordinates.map(coord => [coord.lng, coord.lat])
      }
    };
  };

  // Update map when route data is loaded
  useEffect(() => {
    console.log('🗺️ Mapbox Debug - Route data effect triggered');
    console.log('🗺️ Mapbox Debug - map.current exists:', !!map.current);
    console.log('🗺️ Mapbox Debug - route exists:', !!route);
    console.log('🗺️ Mapbox Debug - mapLoaded:', mapLoaded);

    if (!map.current || !route || !mapLoaded) {
      console.log('🗺️ Mapbox Debug - Route data effect: early return');
      return;
    }

    console.log('🗺️ Mapbox Debug - Processing route data for map...');

    const coordinates = route.coordinates;
    if (coordinates.length === 0) return;

    const bounds = new mapboxgl.LngLatBounds();
    coordinates.forEach(coord => {
      bounds.extend([coord.lng, coord.lat]);
    });

    map.current.fitBounds(bounds, { padding: 50 });

    const routeGeoJSON = convertRouteToGeoJSON(route);

    if (map.current.getSource('route')) {
      (map.current.getSource('route') as mapboxgl.GeoJSONSource).setData(routeGeoJSON);
    } else {
      map.current.addSource('route', {
        type: 'geojson',
        data: routeGeoJSON,
        lineMetrics: true
      });

      // Add route outline
      map.current.addLayer({
        id: 'route-outline',
        type: 'line',
        source: 'route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#ffffff',
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 3,
            12, 4,
            16, 6
          ],
          'line-opacity': 0.8
        }
      });

      // Add main route line
      map.current.addLayer({
        id: 'route',
        type: 'line',
        source: 'route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#EF4444',
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 2,
            12, 3,
            16, 5
          ],
          'line-opacity': 1.0
        }
      });

      // Add hover effects
      map.current.on('mouseenter', 'route', () => {
        if (map.current) {
          map.current.getCanvas().style.cursor = 'pointer';
        }
      });

      map.current.on('mouseleave', 'route', () => {
        if (map.current) {
          map.current.getCanvas().style.cursor = '';
        }
      });

      // Add click handler for route
      map.current.on('click', 'route', (e) => {
        if (e.features && e.features[0] && map.current) {
          const coordinates = e.lngLat;
          const routeCoords = route.coordinates;
          let closestPoint = routeCoords[0];
          let minDistance = Infinity;

          routeCoords.forEach(coord => {
            const distance = Math.sqrt(
              Math.pow(coord.lng - coordinates.lng, 2) +
              Math.pow(coord.lat - coordinates.lat, 2)
            );
            if (distance < minDistance) {
              minDistance = distance;
              closestPoint = coord;
            }
          });

          new mapboxgl.Popup({ closeOnClick: true })
            .setLngLat([coordinates.lng, coordinates.lat])
            .setHTML(`
              <div style="padding: 8px;">
                <strong>Route Elevation</strong><br/>
                Elevation: ${Math.round(closestPoint.elevation)}m<br/>
                Distance: ${closestPoint.distance ? (closestPoint.distance / 1000).toFixed(1) + 'km' : 'N/A'}
              </div>
            `)
            .addTo(map.current);
        }
      });
    }
  }, [route, mapLoaded]);

  // Clear all waypoint markers
  const clearWaypointMarkers = () => {
    waypointMarkers.current.forEach(marker => marker.remove());
    waypointMarkers.current = [];
  };

  // Direction arrows removed

  // Add waypoint markers to map
  const addWaypointMarkers = () => {
    if (!map.current || !route) return;

    clearWaypointMarkers();

    route.waypoints.forEach((waypoint) => {
      const shouldShow = (waypoint.type === 'start' || waypoint.type === 'finish') || showWaypoints;

      if (shouldShow) {
        const element = createWaypointElement(waypoint.type, waypoint);

        const popupContent = `
          <div style="
            padding: 12px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.4;
            min-width: 200px;
          ">
            <div style="
              display: flex;
              align-items: center;
              margin-bottom: 8px;
              font-weight: bold;
              font-size: 16px;
              color: #333;
            ">
              <span style="margin-right: 8px;">${getWaypointIcon(waypoint.type)}</span>
              ${waypoint.name}
            </div>
            <div style="color: #666; font-size: 14px; margin-bottom: 4px;">
              <strong>Type:</strong> ${waypoint.type.replace('_', ' ').toUpperCase()}
            </div>
            ${waypoint.distance ? `
              <div style="color: #666; font-size: 14px; margin-bottom: 4px;">
                <strong>Distance:</strong> ${formatDistance(waypoint.distance)}
              </div>
            ` : ''}
            <div style="color: #666; font-size: 12px; margin-top: 8px;">
              <strong>Coordinates:</strong><br/>
              ${waypoint.lat.toFixed(6)}, ${waypoint.lng.toFixed(6)}
            </div>
          </div>
        `;

        const marker = new mapboxgl.Marker(element)
          .setLngLat([waypoint.lng, waypoint.lat])
          .setPopup(
            new mapboxgl.Popup({
              offset: 30,
              closeButton: true,
              closeOnClick: false,
              maxWidth: '300px'
            }).setHTML(popupContent)
          )
          .addTo(map.current!);

        waypointMarkers.current.push(marker);
      }
    });
  };

  // Direction arrows feature removed

  // Update markers when toggles change
  useEffect(() => {
    if (mapLoaded && route) {
      addWaypointMarkers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWaypoints, mapLoaded, route]);

  // Direction arrows effects removed

  const formatDistance = (km: number): string => {
    if (km == null || isNaN(km)) return '0.0 km';
    return `${km.toFixed(1)} km`;
  };

  const formatElevation = (meters: number): string => {
    if (meters == null || isNaN(meters)) return '0m';
    return `${meters.toLocaleString()}m`;
  };

  const formatGrade = (percentage: number): string => {
    if (percentage == null || isNaN(percentage)) return '0.0%';
    return `${percentage.toFixed(1)}%`;
  };

  const getDifficultyLabel = (difficulty: number): string => {
    if (difficulty <= 1) return 'Easy';
    if (difficulty <= 2) return 'Moderate';
    if (difficulty <= 3) return 'Hard';
    if (difficulty <= 4) return 'Very Hard';
    return 'Extreme';
  };

  const getDifficultyColor = (difficulty: number): 'success' | 'warning' | 'error' => {
    if (difficulty <= 2) return 'success';
    if (difficulty <= 4) return 'warning';
    return 'error';
  };

  const getWaypointIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'start': return '🟢';
      case 'finish': return '🔴';
      case 'feed_zone': return '🍽️';
      case 'sprint': return '💨';
      case 'kom': return '👑';
      default: return '📍';
    }
  };

  // Prepare elevation chart data
  const elevationChartData = route?.coordinates.map((coord, index) => ({
    distance: coord.distance ? (coord.distance / 1000) : (index * (route.totalDistance / route.coordinates.length)),
    elevation: coord.elevation
  })) || [];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !route) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Route not found'}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/routes')}
        >
          Back to Routes
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/routes')}
          sx={{ mb: 2 }}
        >
          Back to Routes
        </Button>

        <Typography variant="h4" gutterBottom>
          {route.name}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Race ID: {route.raceId}
        </Typography>
      </Box>

      {/* Overview Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 250 }}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <RouteIcon color="primary" />
                <Typography variant="h6">Distance</Typography>
              </Stack>
              <Typography variant="h4" color="primary">
                {formatDistance(route.totalDistance)}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: 1, minWidth: 250 }}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TrendingUp color="success" />
                <Typography variant="h6">Elevation Gain</Typography>
              </Stack>
              <Typography variant="h4" color="success.main">
                {formatElevation(route.elevationProfile.totalGain)}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: 1, minWidth: 250 }}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Terrain color="warning" />
                <Typography variant="h6">Max Elevation</Typography>
              </Stack>
              <Typography variant="h4" color="warning.main">
                {formatElevation(route.elevationProfile.maxElevation)}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: 1, minWidth: 250 }}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Timeline color="info" />
                <Typography variant="h6">Climb Segments</Typography>
              </Stack>
              <Typography variant="h4" color="info.main">
                {route.segments.filter(s => s.averageGrade > 3).length}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Route Map */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            <MapIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Route Map
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showWaypoints}
                  onChange={(e) => setShowWaypoints(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {showWaypoints ? <Visibility sx={{ mr: 0.5 }} /> : <VisibilityOff sx={{ mr: 0.5 }} />}
                  Show Waypoints
                </Box>
              }
              labelPlacement="start"
            />
            {/* Direction arrows toggle removed */}
          </Box>
        </Box>
        <Box sx={{ width: '100%', height: 800, borderRadius: 1, overflow: 'hidden' }}>
          <div
            ref={mapContainer}
            style={{ height: '100%', width: '100%' }}
          />
        </Box>
      </Paper>

      {/* Elevation Profile Chart */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Elevation Profile
        </Typography>
        <Box sx={{ width: '100%', height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={elevationChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="distance"
                type="number"
                scale="linear"
                domain={['dataMin', 'dataMax']}
                tickFormatter={(value) => `${value.toFixed(0)} km`}
              />
              <YAxis
                tickFormatter={(value) => `${Math.round(value)}m`}
              />
              <ChartTooltip
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    const distance = Number(label).toFixed(1);
                    const elevation = Math.round(payload[0].value as number);
                    return (
                      <div style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.85)',
                        border: '1px solid #333',
                        borderRadius: '4px',
                        padding: '8px 12px',
                        fontSize: '12px',
                        color: 'white',
                        lineHeight: '1.4'
                      }}>
                        <div style={{ marginBottom: '2px', fontWeight: 'bold' }}>
                          {distance} km
                        </div>
                        <div style={{ color: '#2196f3' }}>
                          {elevation}m elevation
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Area
                type="monotone"
                dataKey="elevation"
                stroke="#2196f3"
                fill="#2196f3"
                fillOpacity={0.3}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      {/* Tables Row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {/* Waypoints Table */}
        <Box sx={{ flex: 1, minWidth: 400 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              <Flag sx={{ mr: 1, verticalAlign: 'middle' }} />
              Waypoints ({route.waypoints.length})
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Distance</TableCell>
                    <TableCell align="right">Position</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {route.waypoints.map((waypoint, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {index + 1}
                      </TableCell>
                      <TableCell>{waypoint.name}</TableCell>
                      <TableCell align="right">
                        {waypoint.distance ? formatDistance(waypoint.distance) : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption">
                          {waypoint.lat?.toFixed(4) || 'N/A'}, {waypoint.lng?.toFixed(4) || 'N/A'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>

        {/* Climb Segments Table */}
        <Box sx={{ flex: 1, minWidth: 400 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              <Timeline sx={{ mr: 1, verticalAlign: 'middle' }} />
              Climb Segments ({route.segments.filter(s => s.averageGrade > 3).length})
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Distance</TableCell>
                    <TableCell align="right">Avg Grade</TableCell>
                    <TableCell align="center">Difficulty</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {route.segments
                    .filter(segment => segment.averageGrade > 3)
                    .map((segment, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {segment.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDistance(segment.startDistance)} - {formatDistance(segment.endDistance)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatDistance(segment.distance)}
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={formatGrade(segment.averageGrade)}
                          size="small"
                          color={segment.averageGrade > 8 ? 'error' : segment.averageGrade > 5 ? 'warning' : 'success'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={getDifficultyLabel(segment.difficulty)}
                          size="small"
                          color={getDifficultyColor(segment.difficulty)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </Box>

      {/* Route Statistics */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          <Info sx={{ mr: 1, verticalAlign: 'middle' }} />
          Route Statistics
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle2">Elevation Details</Typography>
              <Typography variant="body2">
                <TrendingUp sx={{ fontSize: 16, mr: 0.5, color: 'success.main' }} />
                Total Gain: {formatElevation(route.elevationProfile.totalGain)}
              </Typography>
              <Typography variant="body2">
                <TrendingDown sx={{ fontSize: 16, mr: 0.5, color: 'error.main' }} />
                Total Loss: {formatElevation(route.elevationProfile.totalLoss)}
              </Typography>
              <Typography variant="body2">
                <Terrain sx={{ fontSize: 16, mr: 0.5, color: 'warning.main' }} />
                Max: {formatElevation(route.elevationProfile.maxElevation)}
              </Typography>
            </Stack>
          </Box>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle2">Data Points</Typography>
              <Typography variant="body2">
                GPS Coordinates: {route.coordinates.length.toLocaleString()}
              </Typography>
              <Typography variant="body2">
                Waypoints: {route.waypoints.length}
              </Typography>
              <Typography variant="body2">
                Total Segments: {route.segments.length}
              </Typography>
            </Stack>
          </Box>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle2">Difficulty Analysis</Typography>
              <Typography variant="body2">
                Easy Climbs: {route.segments.filter(s => s.difficulty <= 2).length}
              </Typography>
              <Typography variant="body2">
                Hard Climbs: {route.segments.filter(s => s.difficulty > 2 && s.difficulty <= 4).length}
              </Typography>
              <Typography variant="body2">
                Extreme Climbs: {route.segments.filter(s => s.difficulty > 4).length}
              </Typography>
            </Stack>
          </Box>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle2">Route Info</Typography>
              <Typography variant="body2">
                Created: {new Date(route.createdAt).toLocaleDateString()}
              </Typography>
              <Typography variant="body2">
                Updated: {new Date(route.updatedAt).toLocaleDateString()}
              </Typography>
              <Typography variant="body2">
                Race ID: {route.raceId}
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default RouteViewer;